// The current request on the server: its URL, headers and cookies.
//
// src/server.ts runs every Worker request through `runWithRequestState` (src/server/RequestContext.ts),
// with uzeful's per-request cookie store. Cookies set here (e.g. a refreshed ID token from the request
// middleware) are visible to later reads in the same request, including the cookie header forwarded
// to the backend, and are appended to the response as Set-Cookie headers.
//
// In the browser, and outside a request (tests, build-time scripts), every getter returns undefined.
// The store is read from `globalThis`, so this module is safe in client bundles and works from every
// server environment (SSR, and server functions in the RSC environment).
import type { CookieStore } from "uzeful";

export const REQUEST_STATE_KEY = Symbol.for("calmlens:request-state");

export interface RequestState {
  request: Request;
  cookies: CookieStore;
  /** Milliseconds spent in backend requests so far (the `requests` entry of Server-Timing). */
  backendTimeMs?: number;
  /** Backend responses shared within this request (`memoizeForRequest`). */
  memo?: Map<string, Promise<unknown>>;
}

const getState = (): RequestState | undefined => {
  if (typeof window !== "undefined") return undefined;
  const storage = (
    globalThis as { [REQUEST_STATE_KEY]?: { getStore(): RequestState | undefined } }
  )[REQUEST_STATE_KEY];
  return storage?.getStore();
};

/** The incoming request (undefined in the browser or outside a request). */
export const getRequest = (): Request | undefined => getState()?.request;

/** Request URL, parsed (undefined in the browser or outside a request). */
export const getRequestUrl = (): URL | undefined => {
  const request = getRequest();
  return request ? new URL(request.url) : undefined;
};

export const getRequestCookies = (): CookieStore | undefined => getState()?.cookies;

export const getRequestHeaders = (): Headers | undefined => getRequest()?.headers;

/** The request's cookies as a `Cookie` header, including any set earlier in this request. */
export const getRequestCookieHeader = (): string | undefined =>
  getRequestCookies()
    ?.getAll()
    .map(({ name, value }) => `${name}=${encodeURIComponent(value)}`)
    .join("; ");

/**
 * Adds a backend request's duration to the current request's total. Kept in the request state, not a
 * module variable: server functions run in the `rsc` environment's copy of the API client, and
 * concurrent requests share an isolate.
 */
export const addBackendTime = (durationMs: number) => {
  const state = getState();
  if (state) state.backendTimeMs = (state.backendTimeMs ?? 0) + durationMs;
};

/** Milliseconds the current request has spent in backend requests. */
export const getBackendTime = (): number => getState()?.backendTimeMs ?? 0;

/**
 * Runs `load` once per request for `key`: later calls in the same request, from the request
 * middleware or any server function (each environment has its own module copies, so React's
 * `cache()` can't share across them), get the same promise. The key must name everything the
 * result depends on (e.g. the forwarded cookie header). Outside a request, `load` runs every time.
 */
export const memoizeForRequest = <T>(key: string, load: () => Promise<T>): Promise<T> => {
  const state = getState();
  if (!state) return load();
  const memo = (state.memo ??= new Map());
  let promise = memo.get(key) as Promise<T> | undefined;
  if (!promise) {
    promise = load();
    memo.set(key, promise);
  }
  return promise;
};

/** The promise `memoizeForRequest` holds for `key` in this request, if a call has started it. */
export const peekRequestMemo = <T>(key: string): Promise<T> | undefined =>
  getState()?.memo?.get(key) as Promise<T> | undefined;
